import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Drawer, Button, Input, Space, Row, Col, DatePicker, Card, Tag, message, Select } from 'antd';
import { isMobile } from 'react-device-detect';
import BaseSelect from '@/components/Elements/BaseSelect';
import moment from 'moment';
import BaseRadioList from '@/components/Elements/BaseRadios';
import InputMask from "react-input-mask";
import { createCustomerFromProspect } from '@/redux/actions/customerActions';
import { _newLine, genderOptions, appointmentName } from '@/utils/helpers';
import { getListWards } from '@/redux/actions/regionsActions';

const CreateProfileForm = (props) => {
    const [form] = Form.useForm();
    const { visible, currentItem, onCancel, reloadData } = props;

    const [isLoading, setIsLoading] = useState(false);
    const [wards, setWards] = useState([]);
    const [showScheduleField, setShowScheduleField] = useState(false);

    const filterData = useSelector(state => state.filterReducer.list);
    const provinces = filterData?.provinces;
    const customerSources = filterData?.sources;
    const branchList = filterData?.branches;
    const customerStatus = filterData?.statuses;

    const dispatch = useDispatch();

    // Map branch + gender từ currentItem (memo để khỏi tính lại nhiều)
    const computedDefaults = useMemo(() => {
        let branch_id = null;
        let gender = 'khac';

        const genderMale = ['nam', 'Nam'];
        const genderFemale = ['nữ', 'nu', 'Nữ', 'Nu'];

        if (currentItem) {
            const p = (currentItem.province_code || '').toString().trim();
            if (p === '01') branch_id = 2; // Hà Nội
            if (p === '48') branch_id = 1; // Đà Nẵng

            const g = (currentItem.gender || '').toString().trim();
            if (genderMale.includes(g)) gender = 'nam';
            if (genderFemale.includes(g)) gender = 'nu';
        }

        return { branch_id, gender };
    }, [currentItem]);

    // Gộp 1 useEffect: set form + load wards + handle birthday + show schedule
    useEffect(() => {
        if (!currentItem) {
            form.resetFields();
            setWards([]);
            setShowScheduleField(false);
            return;
        }

        const birthday = currentItem.birthday;
        const formattedBirthday =
            birthday && /^\d{4}$/.test(birthday) ? `01/01/${birthday}` : birthday;

        const date = moment(formattedBirthday, "DD/MM/YYYY", true);
        const age = date.isValid() ? moment().diff(date, "years") : "";

        const nextValues = {
            ...currentItem,
            full_name: currentItem?.full_name,
            branch_id: computedDefaults.branch_id,
            gender: computedDefaults.gender,
            phones: currentItem?.phone ? [currentItem.phone] : (currentItem?.phones || []),
            customer_source_id: 48, // default
            card_number: currentItem?.card_number || null,
            schedule_name: 'TUVAN',
            card_status: 'none',
            group_ids: [],
            result_note: _newLine(currentItem?.result_note),
            note: _newLine(currentItem?.note),
            birthday: formattedBirthday,
            age,
        };

        form.setFieldsValue(nextValues);

        // auto show schedule fields nếu status == 3
        const statusId = nextValues.customer_status_id;
        setShowScheduleField(String(statusId) === '3' || Number(statusId) === 3);

        // load wards
        if (nextValues.province_code) {
            dispatch(getListWards({ province_code: nextValues.province_code }))
                .then((res) => setWards(res?.data || []))
                .catch(() => setWards([]));
        } else {
            setWards([]);
        }
    }, [currentItem, computedDefaults.branch_id, computedDefaults.gender, dispatch, form]);

    const onClose = () => {
        onCancel();
        form.resetFields();
        setIsLoading(false);
        setShowScheduleField(false);
        setWards([]);
    };

    const loadWards = async (province_code) => {
        if (!province_code) {
            setWards([]);
            form.setFieldsValue({ ward_code: undefined });
            return;
        }
        try {
            const res = await dispatch(getListWards({ province_code }));
            form.setFieldsValue({ ward_code: undefined });
            setWards(res?.data || []);
        } catch (error) {
            console.error("Lỗi khi tải danh sách phường/xã:", error);
            setWards([]);
        }
    };

    const handleChangeCustomerStatus = (value) => {
        const isSchedule = Number(value) === 3;
        setShowScheduleField(isSchedule);

        // nếu đổi sang trạng thái khác 3 thì clear 2 field lịch để khỏi submit rác
        if (!isSchedule) {
            form.setFieldsValue({ schedule_name: undefined, scheduled_at: null });
        }
    };

    const handleError = () => {
        message.error('Vui lòng điền đầy đủ các trường bắt buộc');
    };

    const handleBirthdayChange = (e) => {
        const value = e.target.value;
        const date = moment(value, "DD/MM/YYYY", true);
        form.setFieldsValue({ age: date.isValid() ? moment().diff(date, "years") : "" });
    };

    const handleSubmit = (values) => {
        if (!currentItem) {
            message.warning('Không có khách hàng tiềm năng để tạo hồ sơ.');
            return;
        }

        setIsLoading(true);

        const payload = { ...values };
        payload.source_type = 'from_prospect';
        payload.prospect_id = currentItem.id;

        // Convert DatePicker -> string cho backend
        if (payload.scheduled_at && moment.isMoment(payload.scheduled_at)) {
            payload.scheduled_at = payload.scheduled_at.format('DD/MM/YYYY HH:mm');
        }

        // Nếu không hiển thị lịch hẹn thì không gửi field lịch
        if (!showScheduleField) {
            delete payload.schedule_name;
            delete payload.scheduled_at;
        }

        console.log("Dữ liệu submit:", payload);

        dispatch(createCustomerFromProspect(payload))
            .then(() => {
                onClose();
                reloadData();
            })
            .catch(() => setIsLoading(false));

    };

    const initialValues = useMemo(() => ({
        customer_source_id: 48,
        card_status: 'none',
        schedule_name: 'TUVAN',
        group_ids: [],
        gender: 'khac',
        phones: [],
        customer_status_id: 3,
        card_number: null,
        birthday: null,
        age: null
    }), []);

    return (
        <Drawer
            open={visible}
            title={`${currentItem ? currentItem?.full_name : ""} - ${currentItem?.code || ""}`}
            onClose={onClose}
            maskClosable={false}
            width={isMobile ? '100%' : '70%'}
            forceRender
            footer={
                <div style={{ textAlign: 'right' }}>
                    <Space>
                        <Button key="back" size="large" onClick={onClose}>
                            Huỷ
                        </Button>
                        <Button
                            loading={isLoading}
                            key="submit"
                            type="primary"
                            size="large"
                            onClick={() => form.submit()}
                        >
                            Tạo hồ sơ
                        </Button>
                    </Space>
                </div>
            }
        >
            {visible && (
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={initialValues}
                    className="form-customer"
                    onFinish={handleSubmit}
                    onFinishFailed={handleError}
                >
                    <Card title={<><Tag color="#0b2b33">1</Tag> Thông tin khách hàng</>} size="small">
                        <Row gutter={16}>
                            <Col xs={24} md={8}>
                                <Form.Item
                                    label="Tên khách hàng"
                                    name="full_name"
                                    rules={[{ required: true, message: 'Bắt buộc nhập' }]}
                                >
                                    <Input />
                                </Form.Item>
                            </Col>

                            <Col xs={24} md={8}>
                                <Form.Item
                                    label="Cơ sở"
                                    name="branch_id"
                                    rules={[{ required: true, message: 'Bắt buộc chọn' }]}
                                >
                                    <BaseSelect
                                        options={branchList || []}
                                        optionValue="id"
                                        optionLabel="name"
                                        showSearch
                                        placeholder="Chọn một bản ghi"
                                        style={{ width: '100%' }}
                                    />
                                </Form.Item>
                            </Col>

                            <Col xs={24} md={8}>
                                <Form.Item
                                    label="Giới tính"
                                    name="gender"
                                    rules={[{ required: true, message: "Bắt buộc chọn" }]}
                                >
                                    <BaseRadioList options={genderOptions || []} name="gender" />
                                </Form.Item>
                            </Col>

                            <Col xs={24} md={16}>
                                <Form.Item
                                    label={<>Số điện thoại <span className='ant-form-item-extra-custom'>(Nhập SĐT, cách nhau bằng dấu phẩy, bắt đầu bằng 0.)</span></>}
                                    name="phones"
                                >
                                    <Select
                                        mode="tags"
                                        style={{ width: '100%' }}
                                        placeholder="Nhập SĐT, cách nhau bằng dấu phẩy"
                                        tokenSeparators={[',']}
                                        open={false}
                                    />
                                </Form.Item>
                            </Col>

                            <Col xs={24} md={8}>
                                <Form.Item
                                    label="Nguồn"
                                    name="customer_source_id"
                                    rules={[{ required: true, message: 'Bắt buộc chọn' }]}
                                >
                                    <BaseSelect
                                        options={customerSources || []}
                                        optionValue="id"
                                        optionLabel="name"
                                        showSearch
                                        placeholder="Chọn một bản ghi"
                                        style={{ width: '100%' }}
                                    />
                                </Form.Item>
                            </Col>

                            <Col xs={24} md={8}>
                                <Form.Item label="Ngày sinh" name="birthday">
                                    <InputMask
                                        mask="99/99/9999"
                                        placeholder="dd/mm/yyyy"
                                        onChange={handleBirthdayChange}
                                    >
                                        {(inputProps) => <Input {...inputProps} />}
                                    </InputMask>
                                </Form.Item>
                            </Col>

                            <Col xs={24} md={8}>
                                <Form.Item label="Tuổi" name="age">
                                    <Input />
                                </Form.Item>
                            </Col>

                            <Col xs={24} md={8}>
                                <Form.Item label="Mã số thẻ cũ" name="card_number">
                                    <Input />
                                </Form.Item>
                            </Col>

                            <Col xs={24} md={8}>
                                <Form.Item
                                    label="Trạng thái khách hàng"
                                    name="customer_status_id"
                                    rules={[{ required: true, message: 'Bắt buộc chọn' }]}
                                >
                                    <BaseSelect
                                        options={customerStatus || []}
                                        optionValue="id"
                                        optionLabel="name"
                                        showSearch
                                        placeholder="Chọn một bản ghi"
                                        style={{ width: '100%' }}
                                        onChange={handleChangeCustomerStatus}
                                    />
                                </Form.Item>
                            </Col>

                            {showScheduleField && (
                                <>
                                    <Col xs={24} md={8}>
                                        <Form.Item
                                            label="Kiểu lịch hẹn"
                                            name="schedule_name"
                                            rules={[{ required: true, message: 'Bắt buộc chọn' }]}
                                        >
                                            <BaseSelect
                                                options={appointmentName || []}
                                                showSearch
                                                placeholder="Chọn một bản ghi"
                                                style={{ width: '100%' }}
                                            />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} md={8}>
                                        <Form.Item
                                            label="Thời gian"
                                            name="scheduled_at"
                                            rules={[{ required: true, message: 'Bắt buộc chọn' }]}
                                        >
                                            <DatePicker
                                                style={{ width: '100%' }}
                                                showTime={{ format: 'HH:mm', defaultValue: moment('00:00', 'HH:mm') }}
                                                format="DD/MM/YYYY HH:mm"
                                            />
                                        </Form.Item>
                                    </Col>
                                </>
                            )}
                        </Row>
                    </Card>

                    <Row gutter={16}>
                        <Col xs={24} md={24}>
                            <Form.Item label="Kết quả tele" name="result_note">
                                <Input.TextArea rows={8} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={24}>
                            <Form.Item label="Ghi chú" name="note">
                                <Input.TextArea rows={4} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col xs={24} md={8}>
                            <Form.Item
                                label="Tỉnh, thành phố"
                                name="province_code"
                                rules={[{ required: true, message: "Bắt buộc chọn" }]}
                            >
                                <BaseSelect
                                    options={provinces || []}
                                    optionValue="code"
                                    optionLabel="name"
                                    showSearch
                                    placeholder="Chọn một bản ghi"
                                    style={{ width: '100%' }}
                                    onChange={loadWards}
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={24} md={8}>
                            <Form.Item label="Xã, phường" name="ward_code">
                                <BaseSelect
                                    options={wards || []}
                                    optionValue="code"
                                    optionLabel="name"
                                    showSearch
                                    placeholder="Chọn một bản ghi"
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={24} md={24}>
                            <Form.Item label="Địa chỉ" name="address">
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            )}
        </Drawer>
    );
};

export default CreateProfileForm;